import Debug from 'debug'
import type { Metadata } from 'grpc-web'
import { RpcError, StatusCode } from 'grpc-web'

import {
  forceRefreshAccessToken,
  getAccessToken,
  getOrRefreshAccessToken,
} from '@jotta/auth-client/useAuthStatus'
import type { ApiErrorResponse } from '@jotta/grpc-web/no/jotta/openapi/api_error'
import { AppError } from '@jotta/types/AppError'
import { GrpcApiError, mapErrorCode } from '@jotta/types/GrpcApiError'
import { enrichError } from '@jotta/utils/error'
import { nextXid } from '@jotta/utils/xid'

const debug = Debug('jotta:grpc')

export function getMeta(
  meta: Metadata = {},
  token?: string,
  deadlineSeconds: number = 10,
): Metadata {
  const deadline = new Date()
  deadline.setSeconds(deadline.getSeconds() + deadlineSeconds)

  const newMeta: Metadata = {
    'X-ID': nextXid(),
    deadline: deadline.getTime().toString(), // used for grpc request timeout ref: https://github.com/grpc/grpc-web#setting-deadline
    ...meta,
  }
  if (!token) {
    token = getAccessToken()
  }
  if (token && !newMeta.Authorization) {
    newMeta.Authorization = `Bearer ${token}`
  }
  return newMeta
}

/**
 * The user's own access tokens recently handed out for requests. A request
 * carrying one of them may be renewed and replayed even when the shared
 * token cache has moved on (another request or tab renewed it meanwhile);
 * any other bearer (a share token) is fixed.
 */
const recentUserTokens: string[] = []
const recentUserTokenLimit = 5

function rememberUserToken(token: string) {
  if (!recentUserTokens.includes(token)) {
    recentUserTokens.push(token)
    recentUserTokens.splice(0, recentUserTokens.length - recentUserTokenLimit)
  }
}

function isUserToken(token: string | undefined) {
  return (
    !!token && (token === getAccessToken() || recentUserTokens.includes(token))
  )
}

export async function getAuthMeta(token?: string): Promise<Metadata> {
  if (!token) {
    token = await getOrRefreshAccessToken()
    if (token) {
      rememberUserToken(token)
    }
  }

  // Do not let getMeta fall back to a cached token after renewal failed.
  if (!token) {
    throw new AppError({
      message: 'Authentication required',
      GrpcStatus: mapErrorCode(StatusCode.UNAUTHENTICATED),
    })
  }

  return getMeta({}, token)
}

function gprcMiddleware(
  namespace: string,
  callName: string,
  originalFunction: Function,
) {
  return function (this: unknown, request: any, meta: Metadata) {
    const logger = debug.extend(`${namespace}:${callName}`)
    const metadata = meta || getMeta(meta)
    const xid = metadata['X-ID']

    const requestObject = request.toObject()
    logger('request', xid, requestObject)

    const call = (callMetadata: Metadata): Promise<any> =>
      originalFunction.call(this, request, callMetadata)
    const promise = call(metadata)

    if (promise.then) {
      const onResponse = (response: any) => {
        logger('response', xid, response.toObject())
        return response
      }
      const onError = (error: unknown) => {
        logger('error.message', (error as Error | undefined)?.message)
        logger('raw error', error)

        if (error instanceof Error) {
          let apiErrorResponse: ApiErrorResponse
          try {
            apiErrorResponse = JSON.parse(error.message) as ApiErrorResponse
          } catch (err) {
            logger('failed to parse ApiErrorResponse', err)
            if (error instanceof RpcError) {
              throw new AppError({
                message: error.message,
                GrpcStatus: mapErrorCode(error.code),
                xid: xid,
                cause: error,
              })
            } else {
              throw enrichError(error, xid, callName)
            }
          }

          const apiError = new GrpcApiError(apiErrorResponse, {
            callName,
            namespace,
            cause: error,
          })
          // debugger
          logger(
            'Throwing ApiError',
            error,
            apiError.code,
            apiError.isNotAuthenticated,
          )
          throw apiError
        }
        logger('Not instance of error, throwing original error', error)
        throw enrichError(error, xid, callName)
      }

      const sentToken = metadata.Authorization?.replace(/^Bearer /, '')
      // Only the user's own token can be renewed; a share token is fixed
      const renewable = isUserToken(sentToken)

      return promise.then(onResponse, async (error: unknown) => {
        if (
          renewable &&
          error instanceof RpcError &&
          error.code === StatusCode.UNAUTHENTICATED
        ) {
          // The timed refresh can run late (a sleeping laptop), so the server
          // may reject a token we still considered valid: renew it and
          // replay the call once, as the Connect transport does.
          const fresh = await forceRefreshAccessToken(sentToken).catch(() => '')
          if (fresh && fresh !== sentToken) {
            logger(
              'Unauthenticated with a stale token: retrying with a new one',
            )
            return call({ ...metadata, Authorization: `Bearer ${fresh}` }).then(
              response => {
                // Record the bearer that authenticated the replay on the caller's
                // metadata, so callers (getPath's download links) use the token
                // that worked.
                metadata.Authorization = `Bearer ${fresh}`
                return onResponse(response)
              },
              onError,
            )
          }
        }
        return onError(error)
      })
    }
    return promise
  }
}

export function withGrpcClientMiddleware<T>(namespace: string, client: T): T {
  if (client) {
    const prototypeOf = Object.getPrototypeOf(client)
    Object.entries(prototypeOf).forEach(([functionName, func]) => {
      if (functionName !== 'constructor') {
        // @ts-ignore
        client[functionName] = gprcMiddleware(namespace, functionName, func)
      }
    })
  }
  return client
}
